import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import './Soluciones.css'; // Import your external stylesheet

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faSquareCheck,
} from '@fortawesome/free-solid-svg-icons';

const translations = {
  es: {
    title: 'Descubre los 5 pasos para optimizar tu proceso de talento',
    steps: [
      {
        number: '01',
        image: '/screenshots/solutions01.png',
        caption: 'Evalua el talento',
        header:
          'Evalúa objetivamente a tu equipo. Descubre sus oportunidades y riesgos',
        description:
          'Nuestra herramienta de evaluación automatiza y objetiva el análisis de los miembros de tu equipo, permitiéndote saber en todo momento dónde enfocar tu atención',
        bullets: [
          'Evaluación de Habilidades con Feedback de Compañeros',
          'Talent Assessment Process (TAP®): Desempeño y Potencial',
          'Evaluación de Riesgos: Probabilidad e Impacto de Rotación',
          'Evaluación de Sucesión',
        ],
        button: 'Descubre más',
      },
      {
        number: '02',
        image: '/screenshots/solutions02.png',
        caption: 'Analiza oportunidades',
        header:
          'Utiliza el framework para evaluar las habilidades de tus empleados y su idoneidad.',
        description:
          'BetherFramework® te permite evaluar la idoneidad de tu empleado a la posición que tiene (o que puede tener en el futuro) y ver cuales son sus oportunidades de crecimiento.',
        bullets: [
          'Framework con 24 competencias y recursos de desarrollo (100% Digital).',
          '+100 perfiles de roles creados. Con su definición y habilidades principales.',
          'TAP® Framework con consejos para desarrollar al talento según su valor para la empresa.',
        ],
        button: 'Descubre más',
      },
      {
        number: '03',
        image: '/screenshots/solutions03.png',
        caption: 'Optimiza el plan de desarrollo',
        header:
          'Modifica junto a tu empleado su plan de desarrollo. Para marcar objetivos alcanzables.',
        description:
          'Nuestra herramienta de inteligencia artificial profundiza en las oportunidades de desarrollo y te ofrece un BetherPlan® diseñado para lograr mejoras en 90 días.',
        bullets: [
          'Planes personalizados adaptados a tus oportunidades específicas.',
          'BetherTools®, acciones concretas para implementar en tu día a día, facilitando el avance continuo de tu plan.',
        ],
        button: 'Descubre más',
      },
      {
        number: '04',
        image: '/screenshots/solutions04.png',
        caption: 'Sigue su evolución',
        header:
          'Acompaña el progreso de tu equipo y corrige el rumbo cuando sea necesario.',
        description:
          'Nuestra herramienta de seguimiento te permite identificar fácilmente quién avanza, quién ha detenido su desarrollo y quién necesita tu apoyo para retomar el camino.',
        bullets: [
          'Seguimiento de hábitos con dinámicas gamificadas.',
          'Panel de Líder: visión integral del progreso de tu equipo',
          'Alertas y notificaciones para que no se te escape ninguna oportunidad de crecimiento',
        ],
        button: 'Descubre más',
      },
      {
        number: '05',
        image: '/screenshots/solutions05.png',
        caption: 'Atiende a las necesidades de tu negocio',
        header:
          'Identifica y mitiga los riesgos de talento de tu organización.',
        description:
          'Nuestro panel de análisis te ofrece una visión en tiempo real del estado de cada equipo, permitiéndote detectar rápidamente riesgos, oportunidades y áreas de mejora.',
        bullets: [
          'TalentPanel®: visión integral de tu empresa y sus riesgos de talento.',
          'Alertas inteligentes: identifica a tiempo riesgos e impactos críticos de rotación.',
          'Gestión de campañas: organiza y supervisa evaluaciones y planes de desarrollo de forma eficiente.',
        ],
        button: 'Descubre más',
      },
    ],
  },
  en: {
    title: 'Discover the 5 Steps to Optimize Your Talent Process',
    steps: [
      {
        number: '01',
        image: '/screenshots/solutions01.png',
        caption: 'Assess talent',
        header:
          'Objectively evaluate your team. Discover their opportunities and risks',
        description:
          'Our assessment tool automates and objectifies the analysis of your team members, allowing you to know at all times where to focus your attention.',
        bullets: [
          'Skills Assessment with Peer Feedback',
          'Talent Assessment Process (TAP®): Performance and Potential',
          'Risk Assessment: Probability and Impact of Turnover',
          'Succession Assessment',
        ],
        button: 'Discover More',
      },
      {
        number: '02',
        image: '/screenshots/solutions02.png',
        caption: 'Analyze opportunities',
        header:
          "Use the framework to assess your employees' skills and suitability.",
        description:
          "BetherFramework® allows you to evaluate your employee's suitability for their current position (or a future one) and identify their growth opportunities.",
        bullets: [
          'Framework with 24 competencies and development resources (100% Digital)',
          '+100 role profiles created, including their definition and key skills',
          'TAP® Framework with insights to develop talent based on their value to the company.',
        ],
        button: 'Discover More',
      },
      {
        number: '03',
        image: '/screenshots/solutions03.png',
        caption: 'Refine the Development Plan',
        header:
          'Modify your employee’s development plan together to set achievable goals.',
        description:
          'Our artificial intelligence tool dives into development opportunities and offers you a BetherPlan® designed to achieve improvements in 90 days.',
        bullets: [
          'Personalized plans tailored to your specific opportunities.',
          'BetherTools®, concrete actions to implement in your daily routine, facilitating the continuous progress of your plan.',
        ],
        button: 'Learn More',
      },
      {
        number: '04',
        image: '/screenshots/solutions04.png',
        caption: 'Track Their Progress',
        header: 'Support your team’s growth and adjust the course when needed.',
        description:
          'Our tracking tool helps you easily identify who is making progress, who has paused their development, and who needs your support to get back on track.',
        bullets: [
          'Habit tracking with gamified dynamics.',
          'Leader Dashboard: comprehensive view of your team’s progress.',
          'Alerts and notifications to ensure you never miss a growth opportunity.',
        ],
        button: 'Learn More',
      },
      {
        number: '05',
        image: '/screenshots/solutions05.png',
        caption: 'Address Your Business Needs',
        header: 'Identify and mitigate talent risks within your organization.',
        description:
          'Our analytics dashboard provides a real-time view of each team’s status, allowing you to quickly detect risks, opportunities, and areas for improvement.',
        bullets: [
          'TalentPanel®: comprehensive view of your company and its talent risks.',
          'Smart alerts: timely identification of critical turnover risks and impacts.',
          'Campaign management: efficiently organize and monitor evaluations and development plans.',
        ],
        button: 'Learn More',
      },
    ],
  },
};

function Soluciones({ language = 'es' }) {
  const currentTranslations = translations[language] || translations['es'];
  const { title, steps } = currentTranslations;

  const containerRef = useRef(null);
  const headerRefs = useRef([]);
  const [circleActive, setCircleActive] = useState(
    Array(steps.length).fill(false)
  );
  const [activeLineHeight, setActiveLineHeight] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const options = { root: null, threshold: 0.5 };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const idx = parseInt(entry.target.dataset.index, 10);
        setCircleActive((prev) => {
          const next = [...prev];
          next[idx] = entry.isIntersecting;
          return next;
        });
      });
    }, options);

    headerRefs.current.forEach((hdr) => hdr && observer.observe(hdr));
    return () => observer.disconnect();
  }, [steps.length]);

  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.getBoundingClientRect().height);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const containerTop = rect.top + window.scrollY;
      const offset = 110;
      const effectiveHeight = rect.height - 2 * offset;
      let fraction =
        (window.scrollY - (containerTop + offset)) / effectiveHeight;
      fraction = Math.min(Math.max(fraction, 0), 1);
      setActiveLineHeight(effectiveHeight * fraction);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [containerHeight]);

  const handleButtonClick = (index) => {
    const paths = ['/analyze', '/framework', '/develop', '/follow', '/grow'];
    const path = paths[index] || '/';
    navigate(path);
    // Always scroll to top after navigation
    window.scrollTo(0, 0);
  };

  return (
    <section className="sol-section">
      <h2 className="sol-title">{title}</h2>
      <div ref={containerRef} className="sol-stepsContainer">
        <div className="sol-verticalLineBase" />
        <div
          className="sol-verticalLineActive"
          style={{ height: `${activeLineHeight}px` }}
        />
        {steps.map((step, i) => (
          <div key={i} className="sol-stepWrapper">
            <div className="sol-circleColumn">
              <div
                className="sol-stepCircle"
                style={{
                  backgroundColor: circleActive[i] ? '#ff2300' : '#999',
                }}
              >
                {step.number}
              </div>
            </div>
            <div className="sol-imageColumn">
              {step.image ? (
                <img
                  src={step.image}
                  alt={`Paso ${step.number}`}
                  className="sol-image"
                />
              ) : (
                <div className="sol-imagePlaceholder" />
              )}
            </div>
            <div className="sol-textColumn">
              <span className="sol-caption">{step.caption}</span>
              <h2
                className="sol-header"
                data-index={i}
                ref={(el) => (headerRefs.current[i] = el)}
              >
                {step.header}
              </h2>
              <p className="sol-description">{step.description}</p>
              <ul className="sol-bullets">
                {step.bullets.map((b, idx) => (
                  <li key={idx}>
                    <FontAwesomeIcon
                      icon={faSquareCheck}
                      style={{ marginRight: '8px', color: '#ff2300' }}
                    />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                className="sol-button"
                onClick={() => handleButtonClick(i)}
              >
                {step.button}
                <FontAwesomeIcon icon={faChevronRight} className="sol-icon" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

Soluciones.propTypes = {
  language: PropTypes.oneOf(['es', 'en']),
};

export default Soluciones;
